import { useState } from 'react';
import { store } from '@/ui/store';
import { useStore } from '@/ui/useStore';
import { rowColToA1, colToLabel } from '@/model/address';
import { prettyField } from '@/data/classify';
import { distinct } from '@/data/aggregate';
import { parseFilter, type FilterOp } from '@/data/filter';
import type { DbColumn, SourceKind } from '@/model/types';
import { columnTitle, defaultColumns, matchesOf } from '@/giraffe/database';
import { DB_AGGS } from '@/ui/dbAggs';
import { SearchSelect } from '@/ui/SearchSelect';

/**
 * The database wizard, after Vectorworks' Edit Report: first say WHICH objects
 * (criteria), then which of their fields become columns. A field can be added
 * twice — area summed in one column and averaged in the next is an ordinary
 * thing to want.
 *
 * What it places is remembered, so the titles can regroup it later; see
 * giraffe/database.ts.
 */

const SOURCES: { id: SourceKind; label: string }[] = [
  { id: 'areas', label: 'Areas' },
  { id: 'bakedSections', label: 'Baked sections' },
  { id: 'rawSections', label: 'Raw sections' },
  { id: 'usages', label: 'Usages' },
];

const OPS: { id: FilterOp; label: string }[] = [
  { id: '=', label: 'is' },
  { id: '!=', label: 'is not' },
  { id: '~', label: 'contains' },
  { id: '>', label: '>' },
  { id: '>=', label: '≥' },
  { id: '<', label: '<' },
  { id: '<=', label: '≤' },
];

interface Criterion {
  field: string;
  op: FilterOp;
  value: string;
}

function toClause(c: Criterion): string {
  // A comma would split the clause in two; quoting keeps it one value.
  const value = /[,]/.test(c.value) && !c.value.includes('"') ? `"${c.value}"` : c.value;
  return `${c.field}${c.op}${value}`;
}

export function DatabasePane(): JSX.Element | null {
  useStore();
  const wizard = store.dbWizard;
  if (!wizard) return null;
  // Keyed, so opening a different database starts from its own query.
  return <Wizard key={wizard.dbId ?? 'new'} dbId={wizard.dbId} />;
}

function Wizard({ dbId }: { dbId: string | null }): JSX.Element {
  const data = store.data();
  const existing = dbId ? store.database(dbId) : undefined;

  const [step, setStep] = useState<1 | 2>(existing ? 2 : 1);
  const [source, setSource] = useState<SourceKind>(existing?.source ?? 'areas');
  const [criteria, setCriteria] = useState<Criterion[]>(() =>
    existing ? parseFilter(existing.criteria.join(', ')).clauses : []
  );
  const fields = data.fields(source);
  const [columns, setColumns] = useState<DbColumn[]>(() => existing?.columns ?? defaultColumns(fields));
  const [groupBy, setGroupBy] = useState<string | null>(existing?.groupBy ?? null);
  const [thenBy, setThenBy] = useState<string | null>(existing?.thenBy ?? null);
  const [search, setSearch] = useState('');
  // The sheet's own filter applies to every database on it — shown, so the
  // count below is not a surprise.
  const extra = store.sheetFilterFor(store.sheet(), source);

  // A criterion with no value yet is unfinished, not "is blank" — which would match nothing.
  const clauses = criteria.filter((c) => c.field.trim() !== '' && c.value.trim() !== '').map(toClause);
  const { rows: matched, errors } = matchesOf({ source, criteria: clauses }, data, extra);
  const q = search.trim().toLowerCase();
  const possible = fields.filter((f) => q === '' || f.toLowerCase().includes(q) || prettyField(f).toLowerCase().includes(q));

  const setCriterion = (i: number, patch: Partial<Criterion>): void =>
    setCriteria(criteria.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const setColumn = (i: number, patch: Partial<DbColumn>): void =>
    setColumns(columns.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const moveColumn = (i: number, by: number): void => {
    const j = i + by;
    if (j < 0 || j >= columns.length) return;
    const next = [...columns];
    [next[i], next[j]] = [next[j]!, next[i]!];
    setColumns(next);
  };

  const save = (): void => {
    const shown = (f: string | null): f is string => f !== null && columns.some((c) => c.field === f);
    const group = shown(groupBy) ? groupBy : null;
    const then = group && shown(thenBy) && thenBy !== group ? thenBy : null;
    store.saveDatabase({ source, criteria: clauses, columns, groupBy: group, thenBy: then }, dbId);
    store.closeDbWizard();
  };

  const where = existing ? rowColToA1(existing.anchor.row, existing.anchor.col) : `row ${store.sel.row}`;

  return (
    <aside className="gt-pane">
      <header className="gt-pane-head">
        <h2>{existing ? 'Edit database' : 'Insert database'}</h2>
        <button className="gt-btn" onClick={() => store.closeDbWizard()} title="Close">
          ×
        </button>
      </header>

      <div className="gt-pane-body">
        <div className="gt-steps">
          <button className={`gt-step${step === 1 ? ' is-on' : ''}`} onClick={() => setStep(1)}>
            1 · Criteria
          </button>
          <button className={`gt-step${step === 2 ? ' is-on' : ''}`} onClick={() => setStep(2)}>
            2 · Columns
          </button>
        </div>

        {step === 1 ? (
          <div className="gt-block-editor">
            <div className="gt-field">
              <label htmlFor="db-src">Find objects in</label>
              <select
                id="db-src"
                className="gt-select"
                value={source}
                onChange={(e) => {
                  const next = e.target.value as SourceKind;
                  setSource(next);
                  setCriteria([]);
                  setColumns(defaultColumns(data.fields(next)));
                  setGroupBy(null);
                }}
              >
                {SOURCES.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="gt-field">
              <label>That meet all of these</label>
              {criteria.length === 0 ? <span className="gt-note">No criteria — every object.</span> : null}
              {criteria.map((c, i) => (
                <CriterionRow
                  key={i}
                  criterion={c}
                  fields={fields}
                  values={c.field ? distinct(data.rows(source), c.field).slice(0, 200).map(String) : []}
                  onChange={(patch) => setCriterion(i, patch)}
                  onRemove={() => setCriteria(criteria.filter((_, j) => j !== i))}
                  onDuplicate={() => setCriteria([...criteria.slice(0, i + 1), { ...c }, ...criteria.slice(i + 1)])}
                />
              ))}
              <button
                className="gt-btn"
                onClick={() =>
                  setCriteria([...criteria, { field: fields.includes('usage') ? 'usage' : fields[0] ?? '', op: '=', value: '' }])
                }
              >
                + Criterion
              </button>
            </div>

            {extra ? (
              <span className="gt-note">
                Plus this sheet's filter: <code>{extra}</code>
              </span>
            ) : null}

            <span className={`gt-note${errors.length > 0 ? ' is-error' : ''}`}>
              {errors.length > 0 ? errors[0] : `Objects that meet the criteria: ${matched.length}`}
            </span>

            <button className="gt-btn gt-btn-primary" onClick={() => setStep(2)}>
              Next: columns →
            </button>
          </div>
        ) : (
          <div className="gt-block-editor">
            <div className="gt-db-cols">
              <div className="gt-field">
                <label>Possible columns</label>
                <input
                  className="gt-input"
                  placeholder="Search fields…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <div className="gt-db-list">
                  {possible.map((f) => (
                    <button
                      key={f}
                      className="gt-db-item"
                      title={`Add ${f}`}
                      onClick={() => setColumns([...columns, { field: f, agg: 'auto' }])}
                    >
                      <span>{prettyField(f)}</span>
                      <span className="gt-db-add">+</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="gt-field">
                <label>Database columns</label>
                {columns.length === 0 ? <span className="gt-note">Add a column from the list.</span> : null}
                {columns.map((c, i) => (
                  <div key={i} className="gt-db-col">
                    <span className="gt-db-letter">{colToLabel((existing?.anchor.col ?? 1) + i)}</span>
                    <span className="gt-db-name" title={c.field}>
                      {columnTitle(c, data)}
                    </span>
                    <select
                      className="gt-select"
                      value={c.agg}
                      title="How summary rows reduce this column"
                      onChange={(e) => setColumn(i, { agg: e.target.value as DbColumn['agg'] })}
                    >
                      {DB_AGGS.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.label}
                        </option>
                      ))}
                    </select>
                    <button className="gt-btn" title="Move left" onClick={() => moveColumn(i, -1)}>
                      ↑
                    </button>
                    <button className="gt-btn" title="Move right" onClick={() => moveColumn(i, 1)}>
                      ↓
                    </button>
                    <button className="gt-btn" title="Remove" onClick={() => setColumns(columns.filter((_, j) => j !== i))}>
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="gt-field">
              <label>Summarize items with the same</label>
              <SearchSelect
                value={groupBy ?? ''}
                onChange={(v) => setGroupBy(v || null)}
                options={[
                  { value: '', label: '— one row per object —' },
                  ...[...new Set(columns.map((c) => c.field))].map((f) => ({ value: f, label: prettyField(f) })),
                ]}
              />
            </div>

            {groupBy ? (
              <div className="gt-field">
                <label>Then by</label>
                <SearchSelect
                  value={thenBy ?? ''}
                  onChange={(v) => setThenBy(v || null)}
                  options={[
                    { value: '', label: '— nothing more —' },
                    ...[...new Set(columns.map((c) => c.field))]
                      .filter((f) => f !== groupBy)
                      .map((f) => ({ value: f, label: prettyField(f) })),
                  ]}
                />
              </div>
            ) : null}

            <span className="gt-note">
              {matched.length} {groupBy ? 'objects, one sub-row per group' : 'objects'}. The database takes row{' '}
              {existing?.anchor.row ?? store.sel.row} and lists them beneath it as {existing?.anchor.row ?? store.sel.row}.1,{' '}
              {existing?.anchor.row ?? store.sel.row}.2, … — outside the sheet's numbering, so rows below never move.
              Click ▾ on the database row to group, sum or remove a column.
            </span>

            {!existing && store.rowIsBusy(store.sel.row, columns.length) ? (
              <span className="gt-note is-warn">
                Row {store.sel.row} is not empty, so a new row is inserted there for the database. Formulas below
                follow the rows they point at.
              </span>
            ) : null}

            <div className="gt-db-actions">
              <button className="gt-btn" onClick={() => setStep(1)}>
                ← Criteria
              </button>
              <button className="gt-btn gt-btn-primary" disabled={columns.length === 0} onClick={save}>
                {existing ? 'Update database' : `Insert on ${where}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}

function CriterionRow({
  criterion,
  fields,
  values,
  onChange,
  onRemove,
  onDuplicate,
}: {
  criterion: Criterion;
  fields: readonly string[];
  values: string[];
  onChange: (patch: Partial<Criterion>) => void;
  onRemove: () => void;
  onDuplicate: () => void;
}): JSX.Element {
  return (
    <div className="gt-db-crit">
      <SearchSelect
        value={criterion.field}
        onChange={(field) => onChange({ field, value: '' })}
        options={fields.map((f) => ({ value: f, label: prettyField(f) }))}
      />
      <select className="gt-select" value={criterion.op} onChange={(e) => onChange({ op: e.target.value as FilterOp })}>
        {OPS.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
      <SearchSelect
        className="gt-db-crit-value"
        value={criterion.value}
        placeholder="value"
        allowCustom
        onChange={(value) => onChange({ value })}
        options={values.map((v) => ({ value: v, label: v }))}
      />
      <button className="gt-btn" title="Duplicate" onClick={onDuplicate}>
        ⧉
      </button>
      <button className="gt-btn" title="Remove" onClick={onRemove}>
        ×
      </button>
    </div>
  );
}
